import { useEffect, useRef, useState } from 'react'
import { api, type UploadedFile } from '../api'
import { ArrowUp, Check, ChevronDown, Close, Document, Plus, Spinner, Stop } from './icons'

interface Props {
  busy: boolean
  models: string[]
  currentModel: string | null
  onModelChange: (model: string) => void
  onSend: (text: string, attachments: UploadedFile[]) => void | Promise<void>
  onStop: () => void
}

/**
 * Model picker. It lives inside the composer rather than in a top bar: the
 * model is chosen right before sending, so it belongs where the hand already
 * is. Built as a menu instead of a native <select> so it can be styled and
 * open upward without clipping.
 */
function ModelPicker({
  models,
  current,
  onChange,
}: {
  models: string[]
  current: string | null
  onChange: (m: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [maxHeight, setMaxHeight] = useState<number>()
  const wrap = useRef<HTMLDivElement>(null)
  const toggle = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  /**
   * Потолок списка — это место НАД кнопкой, и его приходится мерить.
   *
   * Раньше стояло `max-h-[60vh]`, и оно не ограничивало ничего. Четырнадцать
   * моделей — это около 400px; 60vh на webview высотой 700px — это 420px, то
   * есть потолок выше содержимого. Он не срабатывал никогда, прокручивать
   * было нечего, а список всё равно уходил за верхний край, потому что растёт
   * он вверх от кнопки, а кнопка стоит внизу экрана.
   *
   * И `vh` здесь неверная единица в принципе: в мини-приложении это высота
   * всего webview, а не той его части, которую видно, — с открытой клавиатурой
   * они расходятся вдвое. Поэтому меряем реальный зазор от кнопки до верха
   * видимой области и пересчитываем его, пока список открыт: клавиатура
   * появляется и исчезает под ним.
   */
  useEffect(() => {
    if (!open) return
    const measure = () => {
      const el = toggle.current
      if (!el) return
      const vv = window.visualViewport
      const top = el.getBoundingClientRect().top - (vv ? vv.offsetTop : 0)
      // GAP — отступ списка от кнопки (mb-1.5) плюс поля от края экрана.
      const GAP = 14
      // Пол, а не голая разность: если места нет совсем, лучше маленький
      // прокручиваемый список, чем список нулевой высоты.
      setMaxHeight(Math.max(120, Math.round(top - GAP)))
    }
    measure()
    const vv = window.visualViewport
    vv?.addEventListener('resize', measure)
    vv?.addEventListener('scroll', measure)
    window.addEventListener('resize', measure)
    return () => {
      vv?.removeEventListener('resize', measure)
      vv?.removeEventListener('scroll', measure)
      window.removeEventListener('resize', measure)
    }
  }, [open])

  if (models.length === 0) return null

  return (
    <div ref={wrap} className="relative shrink-0">
      <button
        ref={toggle}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex max-w-60 items-center gap-2 rounded-full px-3 py-2 text-xs
                   text-(--ink-dim) transition hover:bg-(--raised-hi) hover:text-(--ink)"
      >
        <span className="truncate">{current ?? 'модель'}</span>
        <ChevronDown size={13} className="shrink-0" />
      </button>

      {open && (
        // Высота приходит из замера выше, а не из класса: `vh` в мини-приложении
        // меряет не то. `overscroll-contain` держит прокрутку внутри списка —
        // в webview телеграма прокрутка страницы позади это жест, закрывающий
        // приложение.
        <ul
          role="listbox"
          style={{ maxHeight }}
          className="absolute right-0 bottom-full z-30 mb-1.5 min-w-52
                     overflow-y-auto overscroll-contain rounded-xl
                     border border-(--hairline) bg-(--composer) p-1.5 shadow-xl"
        >
          {models.map((m) => (
            <li key={m}>
              <button
                type="button"
                role="option"
                aria-selected={m === current}
                onClick={() => {
                  onChange(m)
                  setOpen(false)
                }}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-3 text-left text-sm
                           text-(--ink-dim) transition hover:bg-(--raised) hover:text-(--ink)"
              >
                <span className="w-3.5 shrink-0">
                  {m === current && <Check size={13} />}
                </span>
                <span className="truncate">{m}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function Composer({
  busy,
  models,
  currentModel,
  onModelChange,
  onSend,
  onStop,
}: Props) {
  const [text, setText] = useState('')
  const [pending, setPending] = useState<UploadedFile[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadingNames, setUploadingNames] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const submitting = useRef(false)
  const area = useRef<HTMLTextAreaElement>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const uploadAbort = useRef<AbortController | null>(null)

  useEffect(() => () => uploadAbort.current?.abort(), [])

  // Textarea растёт под текст, но не бесконечно.
  useEffect(() => {
    const el = area.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`
  }, [text])

  const submit = async () => {
    if (busy || uploadAbort.current || submitting.current) return
    if (!text.trim() && pending.length === 0) return
    submitting.current = true
    setSending(true)
    setError(null)
    try {
      await onSend(text, pending)
      setText('')
      setPending([])
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      submitting.current = false
      setSending(false)
    }
  }

  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    if (uploadAbort.current || busy || submitting.current) return
    const selected = Array.from(files)
    setUploading(true)
    setUploadingNames(selected.map((file) => file.name))
    setError(null)
    const controller = new AbortController()
    uploadAbort.current = controller
    let timedOut = false
    const timeout = window.setTimeout(() => {
      timedOut = true
      controller.abort()
    }, 30_000)
    try {
      const saved = await api.upload(selected, controller.signal)
      setPending((prev) => [...prev, ...saved])
    } catch (e) {
      if (controller.signal.aborted && !timedOut) return
      setError(
        timedOut
          ? 'Не удалось загрузить файл за 30 секунд. Попробуйте ещё раз.'
          : e instanceof Error
            ? e.message
            : String(e),
      )
    } finally {
      window.clearTimeout(timeout)
      setUploading(false)
      setUploadingNames([])
      uploadAbort.current = null
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  const canSend = !uploading && !sending && (Boolean(text.trim()) || pending.length > 0)

  return (
    <div className="composer-wrap shrink-0 px-3 sm:px-6">
      <div className="mx-auto w-full max-w-xl">
        {error && (
          <div role="alert" className="mb-2 flex items-center justify-between rounded-2xl border border-(--hairline) bg-(--accent-soft) px-3.5 py-2.5 text-sm text-(--ink)">
            {error}
            <button
              type="button"
              onClick={() => setError(null)}
              className="ml-3 rounded-full p-1 text-(--ink-faint) hover:bg-(--raised-hi) hover:text-(--ink)"
              aria-label="закрыть ошибку"
            >
              <Close size={14} />
            </button>
          </div>
        )}

        <div
          className="composer-surface rounded-[28px] border border-(--hairline) p-2.5"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            void upload(e.dataTransfer.files)
          }}
        >
        {uploadingNames.length > 0 && (
          <div role="status" className="attachment-card mb-2 w-full text-sm text-(--ink-dim)">
            <Spinner size={20} className="shrink-0 animate-spin" />
            <span className="min-w-0 flex-1 truncate">
              загружаю {uploadingNames.join(', ')}…
            </span>
            <button
              type="button"
              onClick={() => uploadAbort.current?.abort()}
              className="shrink-0 text-xs text-(--ink-faint) hover:text-(--ink)"
            >
              отменить
            </button>
          </div>
        )}

        {pending.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {pending.map((f) => (
              <span
                key={f.path}
                 className="attachment-card text-sm text-(--ink)"
              >
                <Document size={24} />
                <span className="min-w-0"><span className="block max-w-48 truncate">{f.name}</span><span className="mt-0.5 block text-xs text-(--ink-faint)">Прикреплён</span></span>
                <button
                  type="button"
                  onClick={() =>
                    setPending((prev) => prev.filter((p) => p.path !== f.path))
                  }
                  className="text-(--ink-faint) transition hover:text-(--ink)"
                  aria-label={`убрать ${f.name}`}
                >
                  <Close size={13} />
                </button>
              </span>
            ))}
          </div>
        )}

          <input
            ref={fileInput}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => void upload(e.target.files)}
          />
          <textarea
            ref={area}
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              // Enter отправляет, Shift+Enter — перенос строки.
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault()
                void submit()
              }
            }}
            placeholder="Спросите о чём угодно"
            aria-label="Сообщение"
            disabled={sending}
            /* text-base below md is not a style choice: iOS Safari force-zooms
               the page when a focused field is under 16px, which is what threw
               the whole layout sideways on a phone. */
            className="block max-h-50 min-h-12 w-full resize-none bg-transparent px-3 pt-2.5 pb-3 text-base leading-7 text-(--ink)
                       outline-none placeholder:text-(--ink-faint)"
          />

          <div className="flex items-center justify-between gap-2">
          <button type="button" onClick={() => fileInput.current?.click()}
            disabled={uploading || busy || sending} title="прикрепить файл" aria-label="прикрепить файл"
            className="chat-action shrink-0 text-(--ink)">
            <Plus size={23} />
          </button>
          <div className="flex min-w-0 items-center gap-2">
          <ModelPicker
            models={models}
            current={currentModel}
            onChange={onModelChange}
          />

          {busy ? (
            <button
              type="button"
              onClick={onStop}
              title="остановить"
              aria-label="остановить"
              className="chat-action send-action shrink-0"
            >
              <Stop size={18} />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!canSend}
              title="отправить"
              aria-label="отправить"
              className="chat-action send-action shrink-0"
            >
              {sending ? <Spinner size={20} className="animate-spin" /> : <ArrowUp size={21} />}
            </button>
          )}
          </div>
          </div>
        </div>
      </div>
    </div>
  )
}
